/* eslint-disable indent */
import React, {useEffect, useMemo, useState} from 'react';
import {Row, Col} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {
  Input,
  Form,
  DropdownField,
  MainLoader,
  ConfirmationModalPromise,
} from '@components';
import {compact, get, uniq, uniqBy} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {Form as FormRender} from 'formik';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {ACTIVITY_CITIES} from '@app/utils/constants';
import {resetFormSubmitSuccess, resetSourceData} from './redux/index';
import {sagaActions} from './redux/saga';

const AddActivity = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const isLoading = useSelector((state) => state.activity.formLoading);
  const parentEntities = useSelector((state) => state.activity.parentEntities);
  const rSourceCategories = useSelector(
    (state) => state.activity.sourceCategories
  );
  const rCategorySources = useSelector(
    (state) => state.activity.categorySources
  );

  const sourceData = useSelector((state) => state.activity.sourceData);
  const formSubmitSuccess = useSelector(
    (state) => state.entity.formSubmitSuccess
  );

  const [sourceCategory, setSourceCategory] = useState(null);
  const [emissionSource, setEmissionSource] = useState(null);

  useEffect(() => {
    if (parentEntities.length === 0) {
      dispatch({
        type: sagaActions.FETCH_ENTITIES,
        payload: {values: {entityType: 'Facility'}},
      });
    }
    if (rSourceCategories.length === 0) {
      dispatch({
        type: sagaActions.FETCH_SOURCE_CATEGORIES,
        payload: {values: {}},
      });
    }
  }, []);

  const localFacilities = useMemo(() => {
    return parentEntities
      .filter((item) => item.type === 'Facility')
      .map((item) => ({value: item.id, label: item.name}));
  }, [parentEntities]);

  const sourceCategories = useMemo(() => {
    return rSourceCategories.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [rSourceCategories]);

  const categorySources = useMemo(() => {
    return rCategorySources.map((item) => ({
      value: item.id,
      label: item.name,
      mainEquation: item.mainEquation,
      subEquation: item.subEquation,
    }));
  }, [rCategorySources]);

  const cities1 = useMemo(() => {
    return uniq(ACTIVITY_CITIES.map((item) => item.parent)).map((item) => ({
      value: item,
      label: item,
    }));
  }, [ACTIVITY_CITIES]);

  useEffect(() => {
    if (formSubmitSuccess) {
      dispatch(resetFormSubmitSuccess(false));
    }
  }, [formSubmitSuccess]);

  useEffect(() => {
    if (sourceCategory) {
      dispatch({
        type: sagaActions.FETCH_CATEGORY_SOURCES,
        payload: {categoryId: sourceCategory.value, values: {}},
      });
    }
  }, [sourceCategory]);

  useEffect(() => {
    if (emissionSource) {
      dispatch({
        type: sagaActions.FETCH_SOURCE_DATA,
        payload: {
          categoryId: sourceCategory.value,
          sourceId: emissionSource.value,
          values: {},
        },
      });
    } else {
      dispatch(resetSourceData({}));
    }
  }, [emissionSource]);

  const {levelFields, initialValues, validationSchema} = useMemo(() => {
    const {fields, data, units} = sourceData;
    const lInitialValues = {};
    const lValidationSchema = {};

    const filteredFields = (fields || []).filter(
      (field) => field.fieldLevel.indexOf('Level') >= 0
    );
    const lLevelFields = filteredFields.map((field) => {
      const fieldParentLevel =
        Number(field.fieldLevel.replace('Level', '')) - 1;
      const parentFields = filteredFields.filter((item) => {
        const fieldCurrentLevel = Number(item.fieldLevel.replace('Level', ''));
        return fieldCurrentLevel <= fieldParentLevel;
      });
      const standardUnits = [];
      const fieldOptions = (data || []).map((rec) => {
        if (field.fieldType === 'Unit') {
          standardUnits.push({
            value: rec.standardUnit,
            label: `${get(
              units.find((unit) => unit.id === Number(rec.standardUnit)),
              'name',
              rec.standardUnit
            )}(standard unit)`,
            parentValues: parentFields.map((pField) => ({
              fieldId: pField.id,
              value: rec.data[pField.id] || '',
              level: pField.fieldLevel,
            })),
          });
        }

        return {
          fieldId: field.id,
          value: rec.data[field.id],
          label:
            field.fieldType === 'Unit'
              ? get(
                  units.find((unit) => unit.id === Number(rec.data[field.id])),
                  'name',
                  rec.data[field.id]
                )
              : rec.data[field.id],
          parentValues: parentFields.map((pField) => ({
            fieldId: pField.id,
            value: rec.data[pField.id] || '',
            level: pField.fieldLevel,
          })),
        };
      });

      lInitialValues[field.fieldLevel] = '';
      lValidationSchema[field.fieldLevel] =
        Yup.object().required('Field is required');

      return {
        id: field.id,
        type: field.fieldType,
        name: field.fieldLevel,
        label: field.fieldName,
        options: fieldOptions,
        parent: fieldParentLevel,
        standardUnits,
      };
    });

    return {
      levelFields: lLevelFields,
      initialValues: lInitialValues,
      validationSchema: lValidationSchema,
    };
  }, [sourceData]);

  const handleCategoryChange = React.useCallback(
    (category, setFieldValue) => {
      if (emissionSource) {
        ConfirmationModalPromise({
          title: `This action will reset the form. do you want to continue?`,
          show: true,
          confirmBtn: 'Yes',
          cancelBtn: 'No',
          headTitle: 'Confirmation!',
        }).then(
          () => {
            setFieldValue('emissionSource', '');
            setFieldValue('co2Result', '');
            setEmissionSource(null);
            setSourceCategory(category);
          },
          () => {
            //  do nothing on failure
          }
        );
      } else {
        setSourceCategory(category);
      }
    },
    [emissionSource, sourceCategory]
  );

  const handleSourceChange = React.useCallback(
    (source, values, setFieldValue) => {
      const filledLevels = compact(
        Object.keys(initialValues).map((fieldKey) => {
          if (fieldKey.indexOf('Level') >= 0) {
            return values[fieldKey] || null;
          }
          return null;
        })
      );
      if (filledLevels.length) {
        ConfirmationModalPromise({
          title: `This action will reset the form. do you want to continue?`,
          show: true,
          confirmBtn: 'Yes',
          cancelBtn: 'No',
          headTitle: 'Confirmation!',
        }).then(
          () => {
            Object.keys(initialValues).map((fieldKey) => {
              if (fieldKey.indexOf('Level') >= 0) {
                setFieldValue(fieldKey, '');
              }
              return fieldKey;
            });
            setEmissionSource(source);
            setFieldValue('co2Result', '');
          },
          () => {
            //  do nothing on failure
          }
        );
      } else {
        setEmissionSource(source);
      }
    },
    [emissionSource, sourceCategory, initialValues]
  );

  const handleCalculateQuantity = React.useCallback(
    (values, setFieldValue) => {
      const {data, units, fields} = sourceData;
      let co2Result = '';
      let userInputQuantity = Number(values.quantity);
      let totalConsumption = '';
      let totalConsumptionUnit = '';

      const {mainEquation, subEquation} = emissionSource;
      // get the selected record combination to apply equations
      const fieldRec = levelFields.reduce((prev, current) => {
        prev[current.id] = {
          fieldId: current.id,
          value: values[current.name] ? values[current.name].value : '',
          type: current.type,
        };
        return prev;
      }, {});
      // console.log('fieldRec', fieldRec);
      const matchedRecord = data.find((rec) => {
        const conditionWithUnit = compact(
          Object.keys(fieldRec).map((fieldId) => {
            let expression = rec.data[fieldId] === fieldRec[fieldId].value;
            if (fieldRec[fieldId].type === 'Unit' && !expression) {
              expression = fieldRec[fieldId].value === rec.standardUnit;
            }
            return expression ? true : null;
          })
        );
        return conditionWithUnit.length === Object.keys(fieldRec).length;
      });

      if (matchedRecord) {
        const standardUnitVal = get(
          units.find((unit) => unit.id === Number(matchedRecord.standardUnit)),
          'name',
          matchedRecord.standardUnit
        );
        const recordUnitFieldId = Number(
          get(
            fields.find((field) => field.fieldType === 'Unit'),
            'id',
            '0'
          )
        );
        // const recordUnit = Number(matchedRecord.data[recordUnitFieldId]);
        const userInputUnit = Number(fieldRec[recordUnitFieldId].value);
        // console.log(
        //   'StandardUnit and user Input Input',
        //   matchedRecord.standardUnit,
        //   userInputUnit
        // );
        // validate if not standard unit then apply conversion
        if (userInputUnit !== Number(matchedRecord.standardUnit)) {
          const [, convOp, convFieldId] = matchedRecord.conversionEq
            ? matchedRecord.conversionEq.split('.')
            : ['', '', ''];
          const convFieldValue = Number(matchedRecord.data[convFieldId]);
          if (convOp === '*') {
            userInputQuantity *= convFieldValue;
          } else if (convOp === '/') {
            userInputQuantity /= convFieldValue;
          } else if (convOp === '+') {
            userInputQuantity += convFieldValue;
          } else if (convOp === '-') {
            userInputQuantity -= convFieldValue;
          }
          // console.log(
          //   'after input conversion',
          //   userInputQuantity,
          //   convFieldValue
          // );
        }

        const [mainEqText, mainEqOp, mainEqFieldId] = mainEquation.split('.');
        const [subEqText, subEqOp, subEqFieldId] = subEquation.split('.');

        totalConsumptionUnit = standardUnitVal;

        if (subEqText !== 'none' && matchedRecord.applySubEquation) {
          const subEqFieldIdVal = Number(matchedRecord.data[subEqFieldId]);
          if (subEqOp === '*') {
            userInputQuantity *= subEqFieldIdVal;
          } else if (subEqOp === '/') {
            userInputQuantity /= subEqFieldIdVal;
          } else if (subEqOp === '+') {
            userInputQuantity += subEqFieldIdVal;
          } else if (subEqOp === '-') {
            userInputQuantity -= subEqFieldIdVal;
          }
          // console.log(
          //   'after apply subEquation',
          //   subEqText,
          //   subEqFieldId,
          //   subEqFieldIdVal,
          //   userInputQuantity
          // );
          // totalConsumptionUnit =
        }

        totalConsumption = Math.ceil(userInputQuantity); // after conversion and apply sub-equation if any

        if (mainEqText !== 'none') {
          const mainEqFieldIdVal = Number(matchedRecord.data[mainEqFieldId]);
          if (mainEqOp === '*') {
            userInputQuantity *= mainEqFieldIdVal;
          } else if (mainEqOp === '/') {
            userInputQuantity /= mainEqFieldIdVal;
          } else if (mainEqOp === '+') {
            userInputQuantity += mainEqFieldIdVal;
          } else if (mainEqOp === '-') {
            userInputQuantity -= mainEqFieldIdVal;
          }
          co2Result = `${Math.ceil(
            userInputQuantity / 1000
          )}(${standardUnitVal})`;
        } else {
          co2Result = userInputQuantity;
        }
      } else {
        co2Result = '';
      }

      // co2Result = matchedRecord ? matchedRecord.standardUnit : 0;
      // console.log('matchedRecord', matchedRecord);
      setFieldValue('co2Result', co2Result);
      setFieldValue('totalConsumption', totalConsumption);
      setFieldValue('totalConsumptionUnit', totalConsumptionUnit);
    },
    [levelFields, sourceData, emissionSource]
  );

  const handleSubmit = (values) => {
    dispatch({type: sagaActions.CREATE_ACTIVITY, payload: {values, history}});
  };

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <Form
        initialValues={{
          description: '',
          parentEntity: '',
          activityYear: moment().year(),
          city1: '',
          city2: '',
          sourceCategory: '',
          emissionSource: '',
          ...initialValues,
          quantity: '',
          co2Result: '',
          totalConsumption: '',
        }}
        validationSchema={Yup.object({
          description: Yup.string().required(
            t('reporter.activity.required.description')
          ),
          parentEntity: Yup.object().required(
            t('reporter.activity.required.parentEntity')
          ),
          city1: Yup.object().required(t('reporter.activity.required.city1')),
          city2: Yup.object().required(t('reporter.activity.required.city2')),
          sourceCategory: Yup.object().required(
            t('reporter.activity.required.sourceCategory')
          ),
          emissionSource: Yup.object().required(
            t('reporter.activity.required.emissionSource')
          ),
          ...validationSchema,
          quantity: Yup.string()
            .required(t('reporter.activity.required.quantity'))
            .test({
              name: 'is-valid-number',
              message: t('reporter.activity.required.invalidQuantity'),
              test: (value) => {
                return value && /^[0-9]+$/i.test(value) ? true : false;
              },
            }),
        })}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
        // enableReinitialize
      >
        {({values, setFieldValue}) => {
          return (
            <FormRender noValidate className="reactForm">
              <Row>
                <Col>
                  <StyledH3>{t('reporter.activity.addNewActivity')}</StyledH3>
                </Col>
              </Row>
              <div className="card">
                <div className="card-body">
                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <Input
                        type="text"
                        name="activityYear"
                        id="activityYear"
                        label={t('reporter.activity.activityYear')}
                        placeholder=""
                        required
                        disabled
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <DropdownField
                        id="parentEntity"
                        name="parentEntity"
                        label={t('reporter.activity.parentEntity')}
                        options={localFacilities}
                        placeholder=""
                        required
                        menuPortalTarget="parent"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <DropdownField
                        id="city1"
                        name="city1"
                        label={t('reporter.activity.city1')}
                        options={cities1}
                        placeholder=""
                        required
                        menuPortalTarget="parent"
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <DropdownField
                        id="city2"
                        name="city2"
                        label={t('reporter.activity.city2')}
                        options={ACTIVITY_CITIES.filter(
                          (item) => item.parent === values.city1.value
                        ).map((item) => ({value: item.name, label: item.name}))}
                        placeholder=""
                        required
                        menuPortalTarget="parent"
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <Input
                        type="text"
                        name="description"
                        id="description"
                        label={t('reporter.activity.description')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <DropdownField
                        id="sourceCategory"
                        name="sourceCategory"
                        label={t('reporter.activity.sourceCategory')}
                        options={sourceCategories}
                        placeholder=""
                        required
                        menuPortalTarget="parent"
                        onChange={(category) =>
                          handleCategoryChange(category, setFieldValue)
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>
                      <DropdownField
                        id="emissionSource"
                        name="emissionSource"
                        label={t('reporter.activity.emissionSource')}
                        options={categorySources}
                        placeholder=""
                        required
                        menuPortalTarget="parent"
                        onChange={(source) =>
                          handleSourceChange(source, values, setFieldValue)
                        }
                      />
                    </Col>
                  </Row>
                  {levelFields.map((field) => {
                    return (
                      <Row key={`level-fields-${field.name}`} className="mb-2">
                        <Col>
                          <DropdownField
                            id={field.name}
                            name={field.name}
                            label={field.label}
                            options={uniqBy(
                              field.options
                                .filter((option) => {
                                  const matchedOptions = compact(
                                    option.parentValues.map((pField) => {
                                      return get(
                                        values[pField.level],
                                        'value',
                                        ''
                                      ) === pField.value
                                        ? true
                                        : null;
                                    })
                                  );
                                  return option.parentValues.length ===
                                    matchedOptions.length
                                    ? true
                                    : false;
                                })
                                .concat(
                                  field.standardUnits.filter((option) => {
                                    const matchedOptions = compact(
                                      option.parentValues.map((pField) => {
                                        return get(
                                          values[pField.level],
                                          'value',
                                          ''
                                        ) === pField.value
                                          ? true
                                          : null;
                                      })
                                    );
                                    return option.parentValues.length ===
                                      matchedOptions.length
                                      ? true
                                      : false;
                                  })
                                ),
                              'value'
                            )}
                            placeholder=""
                            required
                            menuPortalTarget="parent"
                            onChange={(val) => {
                              // reset level fields from current
                              const currentLevel = Number(
                                field.name.replace('Level', '')
                              );
                              const nFieldsValue = {};
                              levelFields.filter((lField) => {
                                const lFieldLevel = Number(
                                  lField.name.replace('Level', '')
                                );
                                if (lFieldLevel > currentLevel) {
                                  setFieldValue(lField.name, '');
                                  nFieldsValue[lField.name] = '';
                                  return true;
                                }
                                return false;
                              });
                              handleCalculateQuantity(
                                {
                                  ...values,
                                  [field.name]: val,
                                  ...nFieldsValue,
                                },
                                setFieldValue
                              );
                            }}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                  <Row className="mb-2">
                    <Col>
                      <Input
                        type="text"
                        name="quantity"
                        id="quantity"
                        label={t('reporter.activity.quantity')}
                        placeholder=""
                        required
                        onChange={(evt) => {
                          const val = evt.target.value;
                          setFieldValue('quantity', val);
                          handleCalculateQuantity(
                            {...values, quantity: val},
                            setFieldValue
                          );
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <input
                        type="hidden"
                        name="totalConsumption"
                        value={values.totalConsumption || ''}
                      />
                      <Input
                        type="text"
                        name="co2Result"
                        id="co2Result"
                        label={t('reporter.activity.co2result')}
                        placeholder=""
                        disabled
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      <GreenButton type="submit" className="mr-1">
                        {t('reporter.activity.addSubmitLabel')}
                      </GreenButton>
                      <WhiteButton
                        type="button"
                        className="mr-1"
                        onClick={() => {
                          history.push('/activities');
                        }}
                      >
                        {t('reporter.activity.cancelSubmitLabel')}
                      </WhiteButton>
                    </Col>
                  </Row>
                </div>
              </div>
            </FormRender>
          );
        }}
      </Form>
    </>
  );
};

export default AddActivity;
